import React, { useState, useEffect, useRef, useCallback } from "react";
import AttachmentModal from "./models/attchment";
import EmojiPickerPopover from "../common/EmojiPickerPopover";
import PollModal from "./models/poll";
import VoiceSendModal from "./models/voicemodel";
import PollResultModal from "./models/pollresult";
import ShareMessageModal from "../common/ShareMessageModal";
import ViewOnceReceivedNotice from "../common/ViewOnceReceivedNotice";
import { resolveMediaUrl } from "../../utils/resolveMediaUrl";
import { resolveMessageCalendarDate } from "../../utils/resolveMessageCalendarDate";
import { LinkifiedText } from "../common/LinkifiedText";
import Cookies from "js-cookie";
import toast from "react-hot-toast";

function RoundUploadSpinner({
  className = "h-12 w-12 border-4",
  trackClassName = "border-gray-300",
  headClassName = "border-t-[#7C4DFF]",
  label = "Uploading",
}) {
  return (
    <div
      className={`${className} ${trackClassName} ${headClassName} shrink-0 rounded-full border-solid animate-spin`}
      style={{ aspectRatio: "1 / 1", boxSizing: "border-box" }}
      aria-label={label}
      role="status"
    />
  );
}

// ---------- Voice Recorder UI (Input Bar Replacement) ----------
const VoiceRecorder = React.forwardRef(({ time, onMicClick, onCancel, onAttachmentClick, attachButtonRef }, ref) => {
  return (
    <div ref={ref} className="flex flex-col items-center justify-center w-full relative z-50">
      <div className="flex justify-around w-full max-w-[460px] absolute -top-12">
        <button ref={attachButtonRef} onClick={(e) => {
          e.stopPropagation();
          onAttachmentClick();
        }}>
          <img
            src="../chatscreen/document.svg"
            alt="attachment"
            className="w-[25px] h-[25px]"
          />
        </button>
        <button onClick={(e) => {
          e.stopPropagation();
          onMicClick();
        }}>
          <img
            src="../chatscreen/recording-mic.svg"
            alt="mic"
            className="w-[56px] h-[56px]"
          />
        </button>
        <button onClick={(e) => {
          e.stopPropagation();
          onCancel();
        }}>
          <img
            src="../chatscreen/message-chat.svg"
            alt="message"
            className="w-[25px] h-[25px]"
          />
        </button>
      </div>
    </div>
  );
});



function MessageMenu({ message, onShare }) {
  const shareType = message?.shareType || message?.original?.share_type || "";
  const isViewOnce = shareType === "sharetoview";
  if (isViewOnce || typeof onShare !== "function") return null;

  return (
    <div className="flex items-center gap-2 h-max bg-white rounded-full shadow-lg border border-gray-200 p-2">
      <button
        onClick={onShare}
        title="Share message"
        className="p-0 hover:bg-purple-50 rounded-lg transition text-gray-600 hover:text-[#7C4DFF]"
      >
        <svg fill="#000000" width="15px" height="15px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M21.707,11.293l-8-8A.99991.99991,0,0,0,12,4V7.54492A11.01525,11.01525,0,0,0,2,18.5V20a1,1,0,0,0,1.78418.62061,11.45625,11.45625,0,0,1,7.88672-4.04932c.0498-.00635.1748-.01611.3291-.02588V20a.99991.99991,0,0,0,1.707.707l8-8A.99962.99962,0,0,0,21.707,11.293ZM14,17.58594V15.5a.99974.99974,0,0,0-1-1c-.25488,0-1.2959.04932-1.56152.085A14.00507,14.00507,0,0,0,4.05176,17.5332,9.01266,9.01266,0,0,1,13,9.5a.99974.99974,0,0,0,1-1V6.41406L19.58594,12Z" />
        </svg>
      </button>
    </div>
  );
}



// ---------- Main Chat Messages Area ----------
export default function MessagesArea ({
  messages,
  contact,
  onBackMobile,
  showVoice,
  voiceTime,
  onMicButton,
  onSendVoiceClick,
  onReturnToInput,
  showAttachModal,
  setShowAttachModal,
  handleAttachPick,
  onVoiceAttachmentClick,
  showVoiceAttachModal,
  setShowVoiceAttachModal,
  showPollModal,
  setShowPollModal,
  showPollResultModal,
  setShowPollResultModal,
  selectedPoll,
  setSelectedPoll,
  message,
  setMessage,
  onSendMessage,
  uploadProgress,
  isUploading,
  handleFileInput,
  groupId,
  onHeaderClick,
  currentUserId,
  allContacts = [],
  onShareMessage,
  onViewOnceMedia,
  onPollUpdated,
}) {
  const containerRef = useRef();
  const emojiRef = useRef();
  const emojiButtonRef = useRef();
  const chatInputRef = useRef();
  const [showEmoji, setShowEmoji] = useState(false);
  const [isMobileLayout, setIsMobileLayout] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 768
  );
  const attachButtonRef = useRef();
  const voiceAttachButtonRef = useRef();
  const voiceRecorderRef = useRef();
  const messagesEndRef = useRef();
  const [imagePreview, setImagePreview] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [viewOncePreview, setViewOncePreview] = useState(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [messageToShare, setMessageToShare] = useState(null);
  const [blurViewOnce, setBlurViewOnce] = useState(false);
  const [isFileDragging, setIsFileDragging] = useState(false);
  const dragDepthRef = useRef(0);
  const groupName = contact?.group_name || contact?.name || "Group";
  const rawGroupImage = contact?.group_image_url || contact?.profile_image || "";
  const groupImageSrc = rawGroupImage
    ? (rawGroupImage.startsWith("http") ? rawGroupImage : `${process.env.REACT_APP_API_BASE_URL}${rawGroupImage}`)
    : "/profile-picture.png";
  const getShareType = (message) => message?.shareType || message?.original?.share_type || "";

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = () => setIsMobileLayout(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const handleEmojiToggle = useCallback((e) => {
    if (isMobileLayout) {
      e.preventDefault();
      chatInputRef.current?.blur();
    }
    setShowEmoji((s) => !s);
  }, [isMobileLayout]);

  useEffect(() => {
    if (isMobileLayout && showEmoji) {
      chatInputRef.current?.blur();
    }
  }, [isMobileLayout, showEmoji]);

  const openShareModal = (msg) => {
    if (typeof onShareMessage !== "function") {
      toast.error("Share action is not available");
      return;
    }
    setMessageToShare(msg);
    setShareModalOpen(true);
  };
  const renderCompactViewOncePill = (label, message, isUser, isUploading = false) => (
    <div className={`relative flex items-center ${isUser ? "flex-row-reverse" : "flex-row"} gap-2 overflow-visible`}>
      <div className={`inline-flex w-fit items-center gap-2 rounded-2xl px-3 py-2 shadow-sm ${isUser ? "bg-[#EDD6FE]" : "bg-white border border-gray-200"}`}>
        {isUploading ? (
          <div className="flex h-6 w-6 items-center justify-center">
            <div className="h-4 w-4 border-2 border-[#7C4DFF]/30 border-t-[#7C4DFF] rounded-full animate-spin" aria-label="Uploading" />
          </div>
        ) : (
          <div className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-dashed border-[#7C4DFF] text-[10px] font-semibold leading-none text-[#7C4DFF]">
            1
          </div>
        )}
        <span className="text-sm font-medium text-gray-900">{label}</span>
        <span className="whitespace-nowrap text-xs text-gray-500">
          {formatMessageTime(message?.original?.created_at || message.time)}
        </span>
      </div>
    </div>
  );
  // Format message time: show only time and UTC offset, e.g. "05:30 PM (UTC+05:30)"
    const formatMessageTime = (raw) => {
        if (!raw && raw !== 0) return "";
        try {
            // Try to create a Date. Accept ISO strings, timestamps, or already formatted strings.
            const d = typeof raw === "number" ? new Date(raw) : new Date(raw);
            if (isNaN(d.getTime())) return String(raw);

            const timeStr = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
            const offsetMinutes = -d.getTimezoneOffset(); // minutes offset from UTC
            const sign = offsetMinutes >= 0 ? "+" : "-";
            const abs = Math.abs(offsetMinutes);
            const hh = String(Math.floor(abs / 60)).padStart(2, "0");
            const mm = String(abs % 60).padStart(2, "0");
            return `${timeStr}`;
        } catch (e) {
            return String(raw);
        }
    };

    // Format date divider
    const formatDateDivider = (date) => {
        if (!date) return "";
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        const isToday = date.getDate() === today.getDate() && 
                        date.getMonth() === today.getMonth() && 
                        date.getFullYear() === today.getFullYear();
        const isYesterday = date.getDate() === yesterday.getDate() && 
                            date.getMonth() === yesterday.getMonth() && 
                            date.getFullYear() === yesterday.getFullYear();

        if (isToday) return "Today";
        if (isYesterday) return "Yesterday";
        
        return date.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
    };

  useEffect(() => {
    if (!viewOncePreview) return;
    setBlurViewOnce(false);

    const handleVisibility = () => {
      setBlurViewOnce(document.hidden);
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [viewOncePreview]);
  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
  }, [messages]);

  // Close emoji picker when clicking outside
  useEffect(() => {
    if (!showEmoji) return;

    const handler = (e) => {
      const inPicker = emojiRef.current?.contains(e.target);
      const onButton = emojiButtonRef.current?.contains(e.target);
      if (!inPicker && !onButton) {
        setShowEmoji(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showEmoji]);

  useEffect(() => {
    const preventDefaultDrop = (event) => {
      const hasFiles = Array.from(event.dataTransfer?.items || []).some((item) => item.kind === "file");
      if (hasFiles) {
        event.preventDefault();
      }
    };

    window.addEventListener("dragover", preventDefaultDrop);
    window.addEventListener("drop", preventDefaultDrop);

    return () => {
      window.removeEventListener("dragover", preventDefaultDrop);
      window.removeEventListener("drop", preventDefaultDrop);
    };
  }, []);

  const handleComposerDragEnter = useCallback((event) => {
    event.preventDefault();
    const hasFiles = Array.from(event.dataTransfer?.items || []).some((item) => item.kind === "file");
    if (!hasFiles) return;
    dragDepthRef.current += 1;
    setIsFileDragging(true);
    // Close file-selection menus while drag-and-drop is active
    setShowAttachModal?.(false);
    setShowVoiceAttachModal?.(false);
    setShowEmoji(false);
  }, [setShowAttachModal, setShowVoiceAttachModal]);

  const handleComposerDragOver = useCallback((event) => {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "copy";
    }
  }, []);

  const handleComposerDragLeave = useCallback((event) => {
    event.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsFileDragging(false);
    }
  }, []);

  const isDroppedImageFile = (file) => {
    if (file?.type?.startsWith("image/")) return true;
    const ext = file?.name?.split(".").pop()?.toLowerCase();
    return ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(ext || "");
  };

  const isDroppedVideoFile = (file) => {
    if (file?.type?.startsWith("video/")) return true;
    const ext = file?.name?.split(".").pop()?.toLowerCase();
    return ["mp4", "webm", "mov", "avi", "mkv", "ogg"].includes(ext || "");
  };

  const handleComposerDrop = useCallback((event) => {
    event.preventDefault();
    dragDepthRef.current = 0;
    setIsFileDragging(false);

    const droppedFiles = Array.from(event.dataTransfer?.files || []);
    const imageFiles = droppedFiles.filter(isDroppedImageFile);
    const videoFiles = droppedFiles.filter(isDroppedVideoFile);

    if (imageFiles.length === 0 && videoFiles.length === 0) {
      toast.error("Drop an image or video to send");
      return;
    }

    if (imageFiles.length > 0) {
      handleFileInput?.(imageFiles.length === 1 ? imageFiles[0] : imageFiles, "photo", "");
    }

    videoFiles.forEach((file) => {
      handleFileInput?.(file, "video", "");
    });
  }, [handleFileInput]);

  // ---------- Message Components ----------
 function TextMessage({ message, isUser, senderName }) {
  const messageText = message?.text ?? "";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`
          relative
          max-w-xs lg:max-w-md
          px-4 py-2 pr-20
          rounded-xl
          text-black
          ${isUser ? "bg-[#EDD6FE]" : "bg-white border border-gray-200"}
        `}
      >
        {!isUser && senderName && (
          <p className="text-xs font-semibold text-gray-700 mb-1">{senderName}</p>
        )}
        {/* Message text */}
        <p className="text-sm font-medium break-words whitespace-pre-wrap">
          <LinkifiedText text={messageText} />
        </p>

        {/* Time */}
        <span
          className="
            absolute
            bottom-2
            right-3
            text-xs
            text-gray-600
            whitespace-nowrap
          "
        >
          {formatMessageTime(message?.original?.created_at)}
        </span>
      </div>
    </div>
  );
}

//   function ImageMessage ({ message, isUser }) {
//     const imageUrl = message.fileUrl 
//       ? (message.fileUrl.startsWith('http') 
//           ? message.fileUrl 
//           : `${process.env.REACT_APP_API_BASE_URL}${message.fileUrl}`)
//       : "../chatscreen/sample-image.jpg";
//     return (
//       <div className={`max-w-md rounded-xl p-2 ${isUser ? "bg-purple-500" : "bg-white border border-gray-200"}`}>
//         {!isUser && (
//           <div className="flex items-center gap-2 mb-2">
//             <img src={message.senderAvatar || "../chatscreen/sample-image.jpg"} alt={message.senderName} className="w-6 h-6 rounded-full" />
//             <span className={`text-xs font-medium  truncate max-w-[200px] ${isUser ? "text-white" : "text-gray-900"}`}>{message.senderName}</span>
//           </div>
//         )}
//         <div className="flex items-center gap-6">
//           <div className="flex items-center gap-3">
//             <div className="flex-shrink-0">
//               <img
//                 src={imageUrl}
//                 alt="Shared content"
//                 className="w-8 h-8 object-cover rounded"
//                 onError={(e) => {
//                   e.target.src = "../chatscreen/sample-image.jpg";
//                 }}
//               />
//             </div>
//             <div className="flex-1 min-w-0">
//               <p className={`text-xs font-medium truncate max-w-[200px] ${isUser ? "text-white" : "text-gray-900"}`}>
//                 {message.fileName || message.text || "Image"}
//               </p>
//               <p className={`text-xs ${isUser ? "text-purple-200" : "text-gray-500"}`}>
//                 {message.fileSize || ""}
//               </p>
//               {message.uploading && (
//                 <div className="flex items-center gap-2 mt-1">
//                   <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
//                     <div
//                       className="h-full bg-purple-700 transition-all duration-300"
//                       style={{ width: `${message.uploadProgress || 0}%` }}
//                     />
//                   </div>
//                   <span className="text-xs text-gray-100">{message.uploadProgress || 0}%</span>
//                   <button
//                     className="ml-1 p-1 hover:bg-gray-100 rounded"
//                     title="Cancel upload"
//                     onClick={message.onCancelUpload}
//                   >
//                     <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-100" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
//                   </button>
//                 </div>
//               )}
//             </div>
//           </div>
//           <div className="flex flex-col items-end">
//             {message.fileUrl && !message.uploading && (
//               <a 
//                 href={imageUrl} 
//                 download={message.fileName}
//                 className="p-1 rounded hover:bg-gray-100 mb-1"
//               >
//                 <img
//                   src="../chatscreen/download.svg"
//                   alt="Download"
//                   className="w-5 h-5"
//                 />
//               </a>
//             )}
//             <div className={`text-xs ${isUser ? "text-purple-200" : "text-gray-500"}`}>{message.time}</div>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   function DocumentMessage ({ message, isUser }) {
//     const downloadUrl = message.fileUrl 
//       ? (message.fileUrl.startsWith('http') 
//           ? message.fileUrl 
//           : `${process.env.REACT_APP_API_BASE_URL}${message.fileUrl}`)
//       : null;
//     return (
//       <div className={`max-w-xs lg:max-w-md rounded-2xl p-3 ${isUser ? "bg-purple-500 text-white" : "bg-white text-gray-900 border border-gray-200"}`}>
//         {!isUser && (
//           <div className="flex items-center gap-2 mb-2">
//             <img src={`${process.env.REACT_APP_API_BASE_URL}${message.senderAvatar}` || "../chatscreen/sample-image.jpg"} alt={message.senderName} className="w-6 h-6 rounded-full" />
//             <span className="text-xs font-medium">{message.senderName}</span>
//           </div>
//         )}
//         <div className="flex items-center gap-3">
//           <div className="p-2 rounded-lg">
//             <img
//               src="../chatscreen/doc.svg"
//               alt="Document"
//               className="w-8 h-8"
//             />
//           </div>
//           <div className="flex-1">
//             <p className={`text-sm font-medium truncate max-w-[200px] ${isUser ? "text-white" : "text-gray-900"}`}>
//               {message.fileName || message.text || "Document"}
//             </p>
//             <p className={`text-xs ${isUser ? "text-purple-200" : "text-gray-500"}`}>
//               {message.fileSize || ""}
//             </p>
//             {message.uploading && (
//               <div className="flex items-center gap-2 mt-1">
//                 <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
//                   <div
//                     className="h-full bg-purple-700 transition-all duration-300"
//                     style={{ width: `${message.uploadProgress || 0}%` }}
//                   />
//                 </div>
//                 <span className="text-xs text-gray-100">{message.uploadProgress || 0}%</span>
//                 <button
//                   className="ml-1 p-1 hover:bg-gray-100 rounded"
//                   title="Cancel upload"
//                   onClick={message.onCancelUpload}
//                 >
//                   <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-100" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
//                 </button>
//               </div>
//             )}
//           </div>
//           {downloadUrl && !message.uploading && (
//             <a 
//               href={downloadUrl} 
//               download={message.fileName}
//               className="p-1 rounded hover:bg-gray-100"
//             >
//               <img
//                 src="../chatscreen/download.svg"
//                 alt="Download"
//                 className="w-5 h-5"
//               />
//             </a>
//           )}
//         </div>
//         <p className={`text-xs mt-2 text-end ${isUser ? "text-purple-200" : "text-gray-500"}`}>{message.time}</p>
//       </div>
//     );
//   }

//  function AudioMessage({ message, isUser }) {
//         const [isPlaying, setIsPlaying] = useState(false);
//         const [currentTime, setCurrentTime] = useState(0);
//         const [duration, setDuration] = useState(0);
//         const audioRef = useRef(null);

//         // Robust time formatting
//         const formatTime = (seconds) => {
//           if (!isFinite(seconds) || seconds < 0) return "0:00";
//           const mins = Math.floor(seconds / 60);
//           const secs = Math.floor(seconds % 60);
//           return `${mins}:${secs.toString().padStart(2, "0")}`;
//         };

//         // WebM duration workaround
//         useEffect(() => {
//           const audio = audioRef.current;
//           if (!audio) return;
//           let triedSeek = false;
//           const updateDuration = () => {
//             if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
//               setDuration(audio.duration);
//             } else if (!triedSeek && message.fileUrl && message.fileUrl.endsWith('.webm')) {
//               triedSeek = true;
//               const originalTime = audio.currentTime;
//               audio.currentTime = 1e10;
//               const resetTime = () => {
//                 audio.currentTime = originalTime || 0;
//                 audio.removeEventListener('durationchange', resetTime);
//               };
//               audio.addEventListener('durationchange', resetTime);
//             }
//           };
//           audio.addEventListener("loadedmetadata", updateDuration);
//           audio.addEventListener("durationchange", updateDuration);
//           // Fallback: try to set duration after a short delay if not available immediately
//           const fallback = setTimeout(() => {
//             updateDuration();
//           }, 700);
//           return () => {
//             audio.removeEventListener("loadedmetadata", updateDuration);
//             audio.removeEventListener("durationchange", updateDuration);
//             clearTimeout(fallback);
//           };
//         }, [message.fileUrl]);

//         // Keep currentTime in sync with audio element
//         useEffect(() => {
//           const audio = audioRef.current;
//           if (!audio) return;
//           const updateTime = () => setCurrentTime(audio.currentTime);
//           audio.addEventListener("timeupdate", updateTime);
//           // Also update duration on play in case it was not set
//           const updateDurationOnPlay = () => {
//             if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
//               setDuration(audio.duration);
//             }
//           };
//           audio.addEventListener("play", updateDurationOnPlay);
//           return () => {
//             audio.removeEventListener("timeupdate", updateTime);
//             audio.removeEventListener("play", updateDurationOnPlay);
//           };
//         }, []);

//         const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

//         const handlePlayClick = () => {
//           const audio = audioRef.current;
//           if (!audio) return;
//           if (isPlaying) {
//             audio.pause();
//           } else {
//             audio.play();
//           }
//         };

//         return (
//           <div className={`flex flex-col ${isUser ? 'justify-end' : 'justify-start'}`}>
//             <div
//               className={`max-w-xs min-w-[150px] lg:max-w-md rounded-2xl p-4 ${isUser
//                 ? "bg-purple-500 text-white"
//                 : "bg-white text-gray-900 border border-gray-200"
//                 }`}
//             >
//               {/* Play Button and Audio Wave */}
//               <div className="gap-3 mb-3">
//                 <button 
//                   onClick={handlePlayClick}
//                   className={`flex-shrink-0 p-2 rounded-full transition ${isUser ? "hover:bg-purple-600" : "hover:bg-gray-100"}`}
//                 >
//                   <img
//                     src={isPlaying ? "../chatscreen/pause.svg" : "../chatscreen/speaker.svg"}
//                     alt={isPlaying ? "Pause" : "Play"}
//                     className="w-8 h-8"
//                   />
//                 </button>
//                 <img
//                   src="../chatscreen/audio-wave.svg"
//                   alt="Audio wave"
//                   className="flex-1 h-10"
//                 />
//               </div>

//               {/* Duration Display */}
//               <div className="text-base font-medium mb-3">
//                 {formatTime(duration)}
//               </div>

//               {/* Message Time */}
//               <p className={`text-xs text-end ${isUser ? "text-purple-200" : "text-gray-500"}`}>
//                 {message.time}
//               </p>
//               <audio
//                 ref={audioRef}
//                 src={message.fileUrl && message.fileUrl.startsWith('http') 
//                   ? message.fileUrl 
//                   : `${process.env.REACT_APP_API_BASE_URL}${message.fileUrl}`}
//                 onPlay={() => setIsPlaying(true)}
//                 onPause={() => setIsPlaying(false)}
//                 onEnded={() => {
//                   setIsPlaying(false);
//                   setCurrentTime(0);
//                 }}
//               />
//             </div>
//             <div className="flex items-center mb-3 mt-3">
//               <span className={`text-xs font-medium min-w-[35px] text-[#5D5FEF]`}>
//                 {formatTime(currentTime)}
//               </span>
//               <div className="flex-1 relative h-4 flex items-center">
//                 {/* Background Bar */}
//                 <div className={`w-full rounded-full h-2 ${isUser ? "bg-purple-400" : "bg-gray-300"}`}></div>
//                 {/* Filled Progress */}
//                 <div
//                   className={`absolute left-0 top-1/2 -translate-y-1/2 rounded-full h-2  bg-[#5D5FEF]`}
//                   style={{ width: `${progress}%` }}
//                 ></div>
//                 {/* Styled slider */}
//                 <input
//                   type="range"
//                   min="0"
//                   max={duration || 0}
//                   value={currentTime}
//                   onChange={(e) => {
//                     const newTime = parseFloat(e.target.value);
//                     setCurrentTime(newTime);
//                     if (audioRef.current) audioRef.current.currentTime = newTime;
//                   }}
//                   className="audio-slider absolute w-full h-4 bg-transparent cursor-pointer text=[#5D5FEF]"
//                 />
//               </div>
//               <span className={`text-xs font-medium min-w-[35px] text-right text-[#5D5FEF]`}>
//                 {formatTime(duration)}
//               </span>
//             </div>
//           </div>
//         );
//       }

 function ImageMessage({ message, isUser, senderName, onShare }) {
    const [imageError, setImageError] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);

    const shareType = getShareType(message);
    const isViewOnce = shareType === "sharetoview";
    const viewedBy = Array.isArray(message.viewedBy) ? message.viewedBy : (Array.isArray(message.original?.viewed_by) ? message.original.viewed_by : []);
    const serverViewed = isViewOnce && viewedBy.some((id) => String(id) === String(currentUserId));
    const isDeleted = Boolean(message.fileDeleted || message.original?.file_deleted);

    const hasFileUrl = Boolean(message.fileUrl);
    const resolvedUrl = hasFileUrl ? resolveMediaUrl(message.fileUrl) : null;
    const fallbackUrl = "../chatscreen/image-file.jpg";
    const isUploading = Boolean(message.uploading);
    const showLoaderPlaceholder = isUploading && (!resolvedUrl || !isLoaded) && !imageError;
    const shouldHideImage = isViewOnce && (isUser || serverViewed || isDeleted);
    const showViewOnceNotice = isViewOnce && !isUser && !serverViewed && !isDeleted && !isUploading;

    if (showViewOnceNotice) {
      return (
        <div className={`relative flex items-center ${isUser ? "flex-row-reverse" : "flex-row"} gap-2 group overflow-visible`}>
          <ViewOnceReceivedNotice
            senderName={senderName}
            time={formatMessageTime(message?.original?.created_at || message.time)}
          />
          {onShare && (
            <div className={`absolute top-1/2 -translate-y-1/2 ${isUser ? "right-full mr-2" : "left-full ml-2"} z-20 opacity-0 pointer-events-none transition-opacity duration-150 group-hover:opacity-100 group-hover:pointer-events-auto`}>
              <MessageMenu message={message} onShare={onShare} />
            </div>
          )}
        </div>
      );
    }

    if (shouldHideImage) {
      return renderCompactViewOncePill("Photo", message, isUser, isUploading);
    }

    const handleOpenImage = () => {
      if (imageError) return;
      setImagePreview(resolvedUrl || fallbackUrl);
    };

    return (
      <div className={`relative flex items-center ${isUser ? "flex-row-reverse" : "flex-row"} gap-2 group overflow-visible`}>
      <div
        className={`max-w-md rounded-xl p-2 ${isUser ? "bg-[#EDD6FE] text-white" : "bg-white border border-gray-200"}`}
      >
        {!isUser && senderName && (
          <p className="text-xs font-semibold text-gray-700 mb-2 px-2 pt-2">{senderName}</p>
        )}
        <div className="flex items-center gap-6">
          <div className="flex flex-col items-center gap-3">
            <div className="flex-shrink-0 relative w-48 h-48">
              {imageError ? (
                <div className="w-full h-full bg-gray-100 rounded-xl flex flex-col items-center justify-center border-2 border-dashed border-gray-300">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12 text-gray-400 mb-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="text-xs text-gray-500 text-center px-4">Image not available</p>
                </div>
              ) : showLoaderPlaceholder ? (
                <div className="w-full h-full rounded-xl bg-gray-100 flex flex-col items-center justify-center border border-gray-200 animate-pulse">
                  <div className="w-10 h-10 border-4 border-gray-300 border-t-[#7C4DFF] rounded-full animate-spin" aria-label="Uploading" />
                  <p className="text-xs text-gray-500 mt-3">Uploading...</p>
                </div>
              ) : (
                <>
                  <img
                    src={resolvedUrl || fallbackUrl}
                    alt="Shared content"
                    className="w-full h-full object-cover rounded-xl transition-opacity cursor-pointer hover:opacity-80"
                    onClick={handleOpenImage}
                    onLoad={() => setIsLoaded(true)}
                    onError={() => setImageError(true)}
                  />
                  {isUploading && (
                    <div className="absolute inset-0 rounded-xl bg-black/30 flex flex-col items-center justify-center">
                      <div className="w-10 h-10 border-4 border-white/30 border-t-white rounded-full animate-spin" aria-label="Uploading" />
                      <p className="text-xs text-white mt-2">Uploading...</p>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="flex-1 min-w-0">
              {isUploading && !showLoaderPlaceholder && (
                <div className="flex items-center gap-2 mt-1">
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-[#7C4DFF] rounded-full animate-spin" aria-label="Uploading" />
                  <span className="text-xs text-gray-500">Uploading...</span>
                  <button
                    className="ml-1 p-1 hover:bg-gray-100 rounded"
                    title="Cancel upload"
                    onClick={message.onCancelUpload}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
            <div className="text-xs text-gray-600 text-end mt-1">{formatMessageTime(message?.original?.created_at)}</div>
      </div>
      {onShare && (
        <div className={`absolute top-1/2 -translate-y-1/2 ${isUser ? "right-full mr-2" : "left-full ml-2"} z-20 opacity-0 pointer-events-none transition-opacity duration-150 group-hover:opacity-100 group-hover:pointer-events-auto`}>
          <MessageMenu message={message} onShare={onShare} />
        </div>
      )}
      </div>
    );
  }

    function DocumentMessage({ message, isUser, senderName, onShare }) {
        // Construct download URL if fileUrl is available
        const downloadUrl = message.fileUrl
            ? (message.fileUrl.startsWith('http')
                ? message.fileUrl
                : `${process.env.REACT_APP_API_BASE_URL}${message.fileUrl}`)
            : null;

        return (
            <div className={`relative flex items-center ${isUser ? "flex-row-reverse" : "flex-row"} gap-2 group overflow-visible`}>
            <div
                className={`max-w-xs lg:max-w-md rounded-2xl p-3 ${isUser
                    ? "bg-[#EDD6FE] text-white"
                    : "bg-white text-gray-900 border border-gray-200"
                    }`}
            >
                {!isUser && senderName && (
                  <p className="text-xs font-semibold text-gray-700 mb-2">{senderName}</p>
                )}
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg">
                        <img
                            src="../chatscreen/doc.svg"
                            alt="Document"
                            className="w-8 h-8"
                        />
                    </div>
                    <div className="flex-1">
                        <p className={`text-sm font-medium truncate max-w-[200px] text-black`}>
                            {message.fileName || message.text || "Document"}
                        </p>
                        <p className={`text-xs text-gray-600`}>
                            {message.fileSize || ""}
                        </p>
                        {message.uploading && (
                            <div className="flex items-center gap-2 mt-1">
                                <div className="w-4 h-4 border-2 border-gray-300 border-t-[#7C4DFF] rounded-full animate-spin" aria-label="Uploading" />
                                <span className="text-xs text-gray-500">Uploading...</span>
                                <button
                                    className="ml-1 p-1 hover:bg-gray-100 rounded"
                                    title="Cancel upload"
                                    onClick={message.onCancelUpload}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>
                        )}
                    </div>
                    {downloadUrl && !message.uploading && (
                        <a
                            href={downloadUrl}
                            download={message.fileName}
                            className="p-1 rounded hover:bg-gray-100"
                        >
                            <img
                                src="../chatscreen/download.svg"
                                alt="Download"
                                className="w-5 h-5"
                            />
                        </a>
                    )}
                </div>
                <p className={`text-xs  text-end text-gray-600`}>{formatMessageTime(message?.original?.created_at)}</p>
            </div>
            {onShare && (
              <div className={`absolute top-1/2 -translate-y-1/2 ${isUser ? "right-full mr-2" : "left-full ml-2"} z-20 opacity-0 pointer-events-none transition-opacity duration-150 group-hover:opacity-100 group-hover:pointer-events-auto`}>
                <MessageMenu message={message} onShare={onShare} />
              </div>
            )}
            </div>
        );
    }

      function VideoMessage({ message, isUser, senderName, onShare }) {
        const videoRef = useRef(null);
        const [isOpen, setIsOpen] = useState(false);
        const [isPlaying, setIsPlaying] = useState(false);
        const [currentTime, setCurrentTime] = useState(0);
        const [duration, setDuration] = useState(0);
        const [volume, setVolume] = useState(1);
        const [isMuted, setIsMuted] = useState(false);
        const [thumbError, setThumbError] = useState(false);
        const [blurViewOnceVideo, setBlurViewOnceVideo] = useState(false);
        const isUploading = Boolean(message.uploading);

        const shareType = getShareType(message);
        const isViewOnce = shareType === "sharetoview";
        const viewedBy = Array.isArray(message.viewedBy) ? message.viewedBy : (Array.isArray(message.original?.viewed_by) ? message.original.viewed_by : []);
        const serverViewed = isViewOnce && viewedBy.some((id) => String(id) === String(currentUserId));
        const isDeleted = Boolean(message.fileDeleted || message.original?.file_deleted);
        const hasFileUrl = Boolean(message.fileUrl);
        const shouldHideVideo = isViewOnce && (isUser || serverViewed || isDeleted);
        const showViewOnceNotice =
          isViewOnce && !isUser && !serverViewed && !isDeleted && !isUploading;

        const resolvedUrl = resolveMediaUrl(message.fileUrl);
        const rawThumbnail =
          message.thumbnail_image ||
          message.thumbnailImage ||
          message.thumbnailUrl ||
          message.thumbnail ||
          message.previewImage ||
          message.original?.thumbnail_image ||
          message.original?.thumbnail_path ||
          null;
        const poster = rawThumbnail
          ? (String(rawThumbnail).startsWith("http")
            ? rawThumbnail
            : `${process.env.REACT_APP_API_BASE_URL}${String(rawThumbnail).startsWith("/") ? rawThumbnail : `/${rawThumbnail}`}`)
          : null;
        const downloadUrl = resolvedUrl;
        const videoTitle = message.fileName || message.text || "Video";

        const closeModal = () => {
          try {
            videoRef.current?.pause();
          } catch (_) {}
          setIsPlaying(false);
          setIsOpen(false);
        };

        const openVideoModal = () => {
          if (!resolvedUrl || shouldHideVideo || showViewOnceNotice) return;
          setIsOpen(true);
        };

        useEffect(() => {
          if (!isOpen || !resolvedUrl) return;
          const t = setTimeout(() => {
            try {
              videoRef.current?.play?.().catch(() => {});
            } catch (_) {
              // ignore autoplay errors
            }
          }, 50);
          return () => clearTimeout(t);
        }, [isOpen, resolvedUrl]);

        useEffect(() => {
          if (!isOpen || !isViewOnce || isUser) return;
          setBlurViewOnceVideo(false);

          const handleVisibility = () => {
            setBlurViewOnceVideo(document.hidden);
          };
          document.addEventListener("visibilitychange", handleVisibility);
          return () => document.removeEventListener("visibilitychange", handleVisibility);
        }, [isOpen, isViewOnce, isUser]);

        useEffect(() => {
          if (!isOpen) return;
          const v = videoRef.current;
          if (!v) return;

          const onTimeUpdate = () => {
            setCurrentTime(v.currentTime || 0);
            setDuration(v.duration && isFinite(v.duration) ? v.duration : 0);
          };
          const onPlay = () => setIsPlaying(true);
          const onPause = () => setIsPlaying(false);
          const onLoaded = () => setDuration(v.duration && isFinite(v.duration) ? v.duration : 0);

          v.volume = volume;
          v.muted = isMuted;
          v.addEventListener("timeupdate", onTimeUpdate);
          v.addEventListener("play", onPlay);
          v.addEventListener("pause", onPause);
          v.addEventListener("loadedmetadata", onLoaded);

          return () => {
            v.removeEventListener("timeupdate", onTimeUpdate);
            v.removeEventListener("play", onPlay);
            v.removeEventListener("pause", onPause);
            v.removeEventListener("loadedmetadata", onLoaded);
          };
        }, [isOpen, isMuted, volume]);

        const togglePlay = () => {
          const v = videoRef.current;
          if (!v) return;
          if (v.paused) {
            v.play().catch(() => {});
          } else {
            v.pause();
          }
        };

        const seekTo = (value) => {
          const v = videoRef.current;
          if (!v || !Number.isFinite(v.duration) || v.duration <= 0) return;
          const nextTime = Math.min(v.duration, Math.max(0, Number(value)));
          v.currentTime = nextTime;
          setCurrentTime(nextTime);
        };

        const changeVolume = (value) => {
          const nextVolume = Math.max(0, Math.min(1, Number(value)));
          setVolume(nextVolume);
          setIsMuted(nextVolume === 0);
          if (videoRef.current) {
            videoRef.current.volume = nextVolume;
            videoRef.current.muted = nextVolume === 0;
          }
        };

        const renderThumbnail = () => {
          if (poster && !thumbError) {
            return (
              <img
                src={poster}
                alt="Video thumbnail"
                className="h-full w-full object-cover"
                onError={() => setThumbError(true)}
              />
            );
          }

          return (
            <div className="h-full w-full bg-gradient-to-br from-[#3B3B4A] via-[#23232F] to-[#111118]" />
          );
        };

        const formatDuration = (seconds) => {
          if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
          const mins = Math.floor(seconds / 60);
          const secs = Math.floor(seconds % 60);
          return `${mins}:${String(secs).padStart(2, "0")}`;
        };

        if (isUploading && !(isViewOnce && isUser)) {
          return (
            <div className={`max-w-md rounded-xl p-2 ${isUser ? "bg-[#EDD6FE] text-white" : "bg-white border border-gray-200"}`}>
              {!isUser && senderName && (
                <p className="text-xs font-semibold text-gray-700 mb-2 px-2 pt-2">{senderName}</p>
              )}
              <div className="flex items-center gap-4">
                <div className="relative flex h-[220px] w-80 max-w-[420px] flex-col items-center justify-center overflow-hidden rounded-[18px] border border-gray-200 bg-gray-100 px-6 text-center">
                  <RoundUploadSpinner label="Uploading video" />
                  <p className="mt-4 text-sm font-medium text-gray-700">Uploading...</p>
                </div>
                <div className="flex min-w-0" />
              </div>
              <p className="text-xs text-end text-gray-600 mt-1">{formatMessageTime(message?.original?.created_at)}</p>
            </div>
          );
        }

        if (showViewOnceNotice) {
          return (
            <div className={`relative flex items-center ${isUser ? "flex-row-reverse" : "flex-row"} gap-2 group overflow-visible`}>
              <ViewOnceReceivedNotice
                senderName={senderName}
                time={formatMessageTime(message?.original?.created_at || message.time)}
              />
              {onShare && (
                <div className={`absolute top-1/2 -translate-y-1/2 ${isUser ? "right-full mr-2" : "left-full ml-2"} z-20 opacity-0 pointer-events-none transition-opacity duration-150 group-hover:opacity-100 group-hover:pointer-events-auto`}>
                  <MessageMenu message={message} onShare={onShare} />
                </div>
              )}
            </div>
          );
        }

        return (
          <>
            {shouldHideVideo ? (
              renderCompactViewOncePill("Video", message, isUser, isUploading)
            ) : (
            <div className={`relative flex items-center ${isUser ? "flex-row-reverse" : "flex-row"} gap-2 group overflow-visible`}>
            <div
              className={`max-w-md rounded-xl p-2 cursor-pointer transition-transform duration-150 hover:scale-[1.01] ${isUser ? "bg-[#EDD6FE] text-white" : "bg-white border border-gray-200"}`}
              onClick={openVideoModal}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") openVideoModal();
              }}
            >
              {!isUser && senderName && (
                <p className="text-xs font-semibold text-gray-700 mb-2 px-2 pt-2">{senderName}</p>
              )}
              <div className="flex items-center gap-4">
                <div
                  className="relative w-80 max-w-[420px] overflow-hidden rounded-[18px] bg-black shadow-lg"
                  style={{ aspectRatio: '16 / 9', maxHeight: '220px' }}
                >
                  <div className="absolute inset-0">{renderThumbnail()}</div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-black/20" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openVideoModal();
                      }}
                      aria-label="Play video"
                      className="flex h-16 w-16 items-center justify-center rounded-full bg-white/95 shadow-[0_10px_30px_rgba(0,0,0,0.25)] transition-transform duration-200 hover:scale-105"
                    >
                      <span className="ml-1 inline-block border-l-[14px] border-y-[10px] border-y-transparent border-l-[#22223a]" />
                    </button>
                  </div>
                </div>
                <div className="flex min-w-0">
                  {/* <p className={`text-sm font-medium truncate max-w-[200px] text-black`}>
                    {videoTitle}
                  </p> */}
                  {/* <p className={`text-xs text-gray-600`}>
                    {message.fileSize || ""}
                  </p> */}
                  {/* {message.uploading && (
                    <div className="flex items-center gap-2 mt-1">
                      <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-purple-500 transition-all duration-300"
                          style={{ width: `${message.uploadProgress || 0}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500">{message.uploadProgress || 0}%</span>
                      <button
                        className="ml-1 p-1 hover:bg-gray-100 rounded"
                        title="Cancel upload"
                        onClick={message.onCancelUpload}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                  )} */}
                </div>
                {/* {downloadUrl && !message.uploading && (
                  <a
                    href={downloadUrl}
                    download={message.fileName}
                    className="p-1 rounded hover:bg-gray-100 self-start"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <img
                      src="../chatscreen/download.svg"
                      alt="Download"
                      className="w-5 h-5"
                    />
                  </a>
                )} */}
              </div>
              <p className={`text-xs text-end text-gray-600 mt-1`}>{formatMessageTime(message?.original?.created_at)}</p>
            </div>
            {onShare && (
              <div className={`absolute top-1/2 -translate-y-1/2 ${isUser ? "right-full mr-2" : "left-full ml-2"} z-20 opacity-0 pointer-events-none transition-opacity duration-150 group-hover:opacity-100 group-hover:pointer-events-auto`}>
                <MessageMenu message={message} onShare={onShare} />
              </div>
            )}
            </div>
            )}

            {isOpen && resolvedUrl && (
              <div
                className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
                onClick={closeModal}
              >
                <div
                  className="w-full max-w-4xl rounded-3xl overflow-hidden border border-white/10 shadow-2xl bg-[#12051f]"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between px-4 py-3 bg-[#fff] border-b border-white/10">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-black truncate">{videoTitle}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {downloadUrl && !(isViewOnce && !isUser) && (
                        <a
                          href={downloadUrl}
                          download={message.fileName}
                          className="px-3 py-2 rounded-full bg-black/10 hover:bg-black/20 text-black text-xs font-medium"
                        >
                          Download
                        </a>
                      )}
                      <button
                        onClick={closeModal}
                        className="w-9 h-9 rounded-full bg-black/10 hover:bg-black/20 flex items-center justify-center"
                        aria-label="Close video"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="bg-black relative">
                    <video
                      ref={videoRef}
                      src={resolvedUrl}
                      poster={poster || undefined}
                      className="w-full max-h-[72vh] object-contain bg-black"
                      style={{ filter: isViewOnce && !isUser && blurViewOnceVideo ? "blur(20px)" : "none" }}
                      playsInline
                      preload="metadata"
                      controlsList={isViewOnce && !isUser ? "nodownload" : undefined}
                    />
                    {isViewOnce && !isUser && blurViewOnceVideo && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-white text-sm font-medium px-4 text-center">
                        Content hidden while app is in background
                      </div>
                    )}
                  </div>

                  <div className="px-4 py-4 bg-[#fff] border-t border-white/10 text-white">
                    <div className="flex items-center gap-3">
                    

                      <div className="flex-1 min-w-0">
                        <input
                          type="range"
                          min="0"
                          max={duration || 0}
                          step="0.01"
                          value={currentTime}
                          onChange={(e) => seekTo(e.target.value)}
                          className="w-full accent-[#7C4DFF]"
                        />
                        <div className="flex items-center justify-between mt-1 text-xs text-black/70">
                          <span>{formatDuration(currentTime)}</span>
                            <button
                        onClick={togglePlay}
                        className="w-8 h-8 rounded-full bg-[#7C4DFF] hover:bg-[#8f63ff] flex items-center justify-center shadow-lg"
                        aria-label={isPlaying ? "Pause" : "Play"}
                      >
                        {isPlaying ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 ml-0.5 text-white" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        )}
                      </button>
                          <span>{formatDuration(duration)}</span>
                        </div>
                      </div>

                      <button
                        onClick={() => setIsMuted((prev) => {
                          const nextMuted = !prev;
                          if (videoRef.current) videoRef.current.muted = nextMuted;
                          return nextMuted;
                        })}
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        aria-label={isMuted ? "Unmute" : "Mute"}
                      >
                        {isMuted ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-[#7C4DFF]" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M16.5 12a4.5 4.5 0 0 0-1.32-3.18l1.41-1.41A6.5 6.5 0 0 1 18 12a6.5 6.5 0 0 1-1.41 4.59l-1.41-1.41A4.5 4.5 0 0 0 16.5 12ZM20 12a7.97 7.97 0 0 1-2.34 5.66l-1.41-1.41A5.97 5.97 0 0 0 18 12a5.97 5.97 0 0 0-1.75-4.25l1.41-1.41A7.97 7.97 0 0 1 20 12Zm-8-8v16l-5-4H3V8h4l5-4Z" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-[#7C4DFF]" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M3 10v4h4l5 4V6L7 10H3Zm13.5 2a4.5 4.5 0 0 0-2.25-3.9v7.8A4.5 4.5 0 0 0 16.5 12Zm0-8a7.96 7.96 0 0 1 4.5 7.2 7.96 7.96 0 0 1-4.5 7.2v-2.35A5.96 5.96 0 0 0 18 12a5.96 5.96 0 0 0-1.5-4.05V4Z" />
                          </svg>
                        )}
                      </button>

                      <div className="w-28">
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.01"
                          value={volume}
                          onChange={(e) => changeVolume(e.target.value)}
  className="w-full accent-[#7C4DFF]"                          aria-label="Volume"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        );
      }

    function AudioMessage({ message, isUser, senderName }) {
        const [isPlaying, setIsPlaying] = useState(false);
        const [currentTime, setCurrentTime] = useState(0);
        const [duration, setDuration] = useState(0);
        const [isHovering, setIsHovering] = useState(false);
        const audioRef = useRef(null);

        // Format time as mm:ss
        const formatTime = (seconds) => {
            if (!isFinite(seconds) || seconds < 0) return "0:00";
            const mins = Math.floor(seconds / 60);
            const secs = Math.floor(seconds % 60);
            return `${mins}:${secs.toString().padStart(2, "0")}`;
        };

        // Update duration on metadata load and when src changes
        useEffect(() => {
            const audio = audioRef.current;
            if (!audio) return;
            let triedSeek = false;
            const updateDuration = () => {
                if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
                    setDuration(audio.duration);
                } else if (!triedSeek && message.fileUrl && message.fileUrl.endsWith('.webm')) {
                    // Try to force duration calculation for webm
                    triedSeek = true;
                    const originalTime = audio.currentTime;
                    // Seek to a very large time to trigger duration
                    audio.currentTime = 1e10;
                    // After durationchange, seek back
                    const resetTime = () => {
                        audio.currentTime = originalTime || 0;
                        audio.removeEventListener('durationchange', resetTime);
                    };
                    audio.addEventListener('durationchange', resetTime);
                }
            };
            audio.addEventListener("loadedmetadata", updateDuration);
            audio.addEventListener("durationchange", updateDuration);
            // Fallback: try to set duration after a short delay if not available immediately
            const fallback = setTimeout(() => {
                updateDuration();
            }, 700);
            return () => {
                audio.removeEventListener("loadedmetadata", updateDuration);
                audio.removeEventListener("durationchange", updateDuration);
                clearTimeout(fallback);
            };
        }, [message.fileUrl]);

        // Keep currentTime in sync with audio element
        useEffect(() => {
            const audio = audioRef.current;
            if (!audio) return;
            const updateTime = () => setCurrentTime(audio.currentTime);
            audio.addEventListener("timeupdate", updateTime);
            // Also update duration on play in case it was not set
            const updateDurationOnPlay = () => {
                if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
                    setDuration(audio.duration);
                }
            };
            audio.addEventListener("play", updateDurationOnPlay);
            return () => {
                audio.removeEventListener("timeupdate", updateTime);
                audio.removeEventListener("play", updateDurationOnPlay);
            };
        }, []);
        const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

        // Play/pause logic
        const handlePlayClick = () => {
            const audio = audioRef.current;
            if (!audio) return;
            if (isPlaying) {
                audio.pause();
            } else {
                audio.play();
            }
        };

        return (
            <div className={`flex flex-col ${isUser ? 'justify-end' : 'justify-start'}`}>
                <div
                    className={`max-w-xs min-w-[150px] lg:max-w-md rounded-2xl p-4 text-black ${isUser
                        ? "bg-[#EDD6FE]"
                        : "bg-white  border border-gray-200"
                        }`}
                >
                    {!isUser && senderName && (
                      <p className="text-xs font-semibold text-gray-700 mb-2">{senderName}</p>
                    )}
                    {/* Play Button and Audio Wave */}
                    <div className="gap-3 mb-3">
                        <button
                            onClick={handlePlayClick}
                            onMouseEnter={() => setIsHovering(true)}
                            onMouseLeave={() => setIsHovering(false)}
                            className={`flex-shrink-0 p-2 rounded-full transition ${isUser ? "hover:bg-[#6938EF]" : "hover:bg-gray-100"}`}
                        >
                            <img
                                src={
                                    isPlaying 
                                        ? ("../chatscreen/pause.svg")
                                        : (isHovering && isUser  ? "../chatscreen/volume-white.svg" : "../chatscreen/speaker.svg")
                                }
                                alt={isPlaying ? "Pause" : "Play"}
                                className="w-8 h-8"
                            />
                        </button>
                        <img
                            src="../chatscreen/audio-wave.svg"
                            alt="Audio wave"
                            className="flex-1 h-10"
                        />
                    </div>

                    {/* Duration Display */}
                    <div className="text-base font-medium mb-1">
                        {formatTime(duration)}
                    </div>

                    {/* Message Time */}
                    <p className={`text-xs text-end text-gray-600`}>
                        {formatMessageTime(message?.original?.created_at)}
                    </p>
                    <audio
                        ref={audioRef}
                        src={
                            message.fileUrl?.startsWith("http")
                                ? message.fileUrl
                                : `${process.env.REACT_APP_API_BASE_URL}${message.fileUrl}`
                        }
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                        className="hidden"
                    />
                    {/* <input
                        type="range"
                        min={0}
                        max={duration || 0}
                        step="0.01"
                        value={currentTime}
                        onChange={(e) => {
                            const newTime = parseFloat(e.target.value);
                            setCurrentTime(newTime);
                            if (audioRef.current) audioRef.current.currentTime = newTime;
                        }}
                        className="audio-slider w-full h-4 bg-transparent cursor-pointer text-[#5D5FEF]"
                    /> */}
                    </div>
                  <div className="flex items-center mb-3 mt-3">
              <span className={`text-xs font-medium min-w-[35px] text-[#5D5FEF]`}>
                {formatTime(currentTime)}
              </span>
              <div className="flex-1 relative h-4 flex items-center">
                {/* Background Bar */}
                <div className={`w-full rounded-full h-2 ${isUser ? "bg-purple-400" : "bg-gray-300"}`}></div>
                {/* Filled Progress */}
                <div
                  className={`absolute left-0 top-1/2 -translate-y-1/2 rounded-full h-2  bg-[#5D5FEF]`}
                  style={{ width: `${progress}%` }}
                ></div>
                {/* Styled slider */}
                <input
                  type="range"
                  min="0"
                  max={duration || 0}
                  value={currentTime}
                  step="0.01"
                  onChange={(e) => {
                    const newTime = parseFloat(e.target.value);
                    setCurrentTime(newTime);
                    if (audioRef.current) audioRef.current.currentTime = newTime;
                  }}
                  className="audio-slider absolute w-full h-4 bg-transparent cursor-pointer text=[#5D5FEF]"
                />
              </div>
              <span className={`text-xs font-medium min-w-[35px] text-right text-[#5D5FEF]`}>
                {formatTime(duration)}
              </span>
            </div>
                </div>
        );
    }

  function PollMessage ({ message, isUser, senderName }) {
    // Handle both server format (poll.options with count) and local format (options with votes)
    const pollData = message.poll || message;
    const options = Array.isArray(pollData.options) ? pollData.options : (Array.isArray(message.options) ? message.options : []);
    const totalVotes =
      Number(pollData.total_votes) ||
      options.reduce((sum, option) => {
        const n = Number(option?.count ?? option?.votes ?? 0) || 0;
        return sum + n;
      }, 0);
    const question = pollData.question || message.question || "Poll";
    const pollId = String(pollData.poll_id ?? pollData.id ?? message.id ?? message.original?.id ?? "unknown_poll");
    const allowMultiple = pollData.allow_multiple === 1 || pollData.allowMultiple === true;
    const isExpired =
      pollData.is_expired === true ||
      pollData.timerexpired === true ||
      pollData.timer_expired === true ||
      pollData.is_active === 0;
    const myChoices = Array.isArray(pollData.my_choices)
      ? pollData.my_choices
      : (pollData.my_choices != null ? [pollData.my_choices] : []);

    const getPollDurationLabel = () => {
      return pollData?.timer || "";
    };

    const pollDurationLabel = getPollDurationLabel();
    const messageTime = formatMessageTime(message?.original?.created_at);

    // Normalize choice to a number (or null) — backend uses 0-based indices
    const toChoiceIndex = (val) => {
      if (val === undefined || val === null) return null;
      const num = typeof val === "number" ? val : parseInt(val, 10);
      return Number.isNaN(num) ? null : num;
    };

    const getOptionIndex = (option, fallbackIndex) => {
      const fromOption = toChoiceIndex(option?.index);
      return fromOption != null ? fromOption : fallbackIndex;
    };

    // Prefer server my_choices; fallback to localStorage only if server has none yet
    const getStoredVotes = () => {
      try {
        const storedVotes = localStorage.getItem("pollVotes");
        if (storedVotes) {
          const votes = JSON.parse(storedVotes);
          return Array.isArray(votes[pollId])
            ? votes[pollId]
            : (votes[pollId] !== undefined ? [votes[pollId]] : []);
        }
      } catch (_) {}
      return [];
    };

    const storeVotes = (choices) => {
      try {
        const storedVotes = localStorage.getItem("pollVotes");
        const votes = storedVotes ? JSON.parse(storedVotes) : {};
        votes[pollId] = choices;
        localStorage.setItem("pollVotes", JSON.stringify(votes));
      } catch (_) {}
    };

    const hasServerChoices = Array.isArray(pollData.my_choices);
    const storedVotes = getStoredVotes();
    const initialVotes = hasServerChoices
      ? myChoices
      : (myChoices.length > 0 ? myChoices : storedVotes);
    const userHasVoted = Boolean(initialVotes.length > 0);

    const [voted, setVoted] = useState(userHasVoted);
    const [voteIndices, setVoteIndices] = useState(
      initialVotes.map(toChoiceIndex).filter((v) => v !== null)
    );
    const [loadingVote, setLoadingVote] = useState(false);
    const lastSyncedChoicesRef = useRef(
      JSON.stringify(initialVotes.map(toChoiceIndex).filter((v) => v !== null).sort((a, b) => a - b))
    );

    // Only sync checkbox selection when THIS user's my_choices actually change.
    // Ignore live count updates so other votes don't blink the checkbox.
    useEffect(() => {
      if (!Array.isArray(pollData.my_choices) && pollData.my_choices == null) {
        return;
      }
      const serverChoices = Array.isArray(pollData.my_choices)
        ? pollData.my_choices
        : [pollData.my_choices];
      const normalizedVotes = serverChoices.map(toChoiceIndex).filter((v) => v !== null);
      const signature = JSON.stringify([...normalizedVotes].sort((a, b) => a - b));
      if (signature === lastSyncedChoicesRef.current) return;

      lastSyncedChoicesRef.current = signature;
      setVoted(normalizedVotes.length > 0);
      setVoteIndices(normalizedVotes);
      storeVotes(normalizedVotes);
    }, [pollId, JSON.stringify(pollData.my_choices)]);

    const applyVoteResponsePoll = (data) => {
      const responsePoll =
        data?.data?.[0]?.poll ||
        data?.data?.poll ||
        data?.poll ||
        null;
      if (responsePoll && typeof onPollUpdated === "function") {
        const messageId =
          message.id ||
          message.original?.message_id ||
          (responsePoll.poll_id ? `poll_${responsePoll.poll_id}` : null);
        onPollUpdated(responsePoll, messageId);
        if (Array.isArray(responsePoll.my_choices)) {
          const normalized = responsePoll.my_choices.map(toChoiceIndex).filter((v) => v !== null);
          lastSyncedChoicesRef.current = JSON.stringify([...normalized].sort((a, b) => a - b));
          storeVotes(normalized);
          setVoted(normalized.length > 0);
          setVoteIndices(normalized);
        }
        return true;
      }
      return false;
    };

    const handleVote = async (choiceIdx) => {
      if (loadingVote || isExpired) return;

      // For multi-select, singular choice toggles on the server
      let optimisticVotes;
      if (allowMultiple) {
        if (voteIndices.includes(choiceIdx)) {
          optimisticVotes = voteIndices.filter((v) => v !== choiceIdx);
        } else {
          optimisticVotes = [...voteIndices, choiceIdx];
        }
      } else {
        if (voteIndices.includes(choiceIdx)) return;
        optimisticVotes = [choiceIdx];
      }

      setLoadingVote(true);
      try {
        const token = Cookies.get("auth_token");
        if (!token) throw new Error("Not authenticated");

        const groupIdForVote = message.original?.group_id || message.group_id || groupId;
        const params = new URLSearchParams();
        params.append("group_id", groupIdForVote);
        params.append("poll_id", pollId);
        // 0-based index; singular choice toggles on multi-select polls
        params.append("choice", String(choiceIdx));

        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/group/poll/vote`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: params.toString(),
        });
        const data = await res.json();
        if (data.Status === 1 || data.status === 1 || data.status === "1" || data.Status === "1") {
          toast.success(voted ? "Vote updated!" : "Vote submitted!");
          if (!applyVoteResponsePoll(data)) {
            // Optimistic local update until socket broadcast arrives
            lastSyncedChoicesRef.current = JSON.stringify([...optimisticVotes].sort((a, b) => a - b));
            storeVotes(optimisticVotes);
            setVoted(optimisticVotes.length > 0);
            setVoteIndices(optimisticVotes);
          }
        } else {
          toast.error(data.message || "Failed to vote");
        }
      } catch (err) {
        toast.error(err.message || "Failed to vote");
      } finally {
        setLoadingVote(false);
      }
    };

    const handleViewVote = () => {
      setSelectedPoll(message);
      setShowPollResultModal(true);
    };

    return (
      <div className={`min-w-xs w-72 max-w-full rounded-2xl p-4 text-black overflow-hidden ${isUser ? "bg-[#EDD6FE]" : "bg-white border border-gray-200"}`}>
        {!isUser && senderName && (
          <p className="text-xs font-semibold text-gray-700 mb-2">{senderName}</p>
        )}

        <h3 className="font-semibold mb-3 text-black break-words break-all">{question}</h3>

        {isExpired && (
          <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-xs font-medium text-red-600">🕐 Poll Expired</p>
          </div>
        )}

        {allowMultiple && !isExpired && (
          <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs font-medium text-blue-600">✓ Multiple selections allowed</p>
          </div>
        )}

        <div className="space-y-2">
          {options.map((option, index) => {
            const optionIndex = getOptionIndex(option, index);
            const voteCount = Number(option?.count ?? option?.votes ?? 0) || 0;
            const percentageFloat = totalVotes > 0 ? (voteCount / totalVotes) * 100 : 0;
            const percentageDisplay = totalVotes > 0 ? `${percentageFloat.toFixed(1)}%` : "0%";
            const percentageWidth = Math.max(0, Math.min(100, percentageFloat));
            const isSelected = voteIndices.includes(optionIndex);
            const optionLabel =
              option?.text ?? option?.title ?? option?.label ?? option?.name ?? option?.option ?? String(option ?? "");

            return (
              <div key={optionIndex} className="relative">
                <div className="flex justify-between items-start gap-2 mb-1">
                  <button
                    disabled={loadingVote || isExpired}
                    onClick={() => handleVote(optionIndex)}
                    className={`flex items-start gap-2 text-sm text-left min-w-0 flex-1 break-words break-all text-black ${isSelected ? "font-bold" : ""} ${isExpired ? "opacity-60" : ""}`}
                    style={{
                      cursor: loadingVote || isExpired ? "not-allowed" : "pointer",
                      background: "none",
                      border: "none",
                      padding: 0,
                    }}
                  >
                    <span
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        isSelected ? "border-purple-500 bg-purple-500" : "border-gray-400"
                      }`}
                    >
                      {isSelected && <span className="w-2 h-2 rounded-full bg-white"></span>}
                    </span>
                    {optionLabel}
                  </button>
                  <span className="text-xs text-black shrink-0">{percentageDisplay}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-purple-500 h-2 rounded-full"
                    style={{ width: `${percentageWidth}%` }}
                  ></div>
                </div>
                <span className={`text-xs block mt-1 text-black`}>{voteCount} votes</span>
              </div>
            );
          })}
        </div>

        <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between gap-3">
          <div>
            <button
              className={`text-sm font-medium ${isExpired ? "text-gray-400 cursor-not-allowed" : "text-purple-600 cursor-pointer"}`}
              onClick={handleViewVote}
              disabled={isExpired}
            >
              View Vote
            </button>
            <p className={`text-xs text-black text-left`}>{totalVotes} votes</p>
          </div>
          <div>
            {pollDurationLabel && !isExpired && (
              <div className="w-max px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="text-xs font-medium text-purple-700">⏱ {pollDurationLabel}</p>
              </div>
            )}
            {isExpired && <p className="text-xs text-red-600">Expired</p>}
            {messageTime && (
              <p className="mt-1 text-right text-[11px] text-gray-500">{messageTime}</p>
            )}
          </div>
        </div>
      </div>
    );
  }
  // ---------- Main Message Renderer ----------
  function MessageRenderer ({ message, senderName }) {
    const isUser = message.isUser;
    const onShare = typeof onShareMessage === "function" && isUser ? () => openShareModal(message) : undefined;
    const resolvedMime = String(message.mime || message.original?.mime_type || message.original?.mime || "").toLowerCase();
    const resolvedFileUrl = message.fileUrl || message.original?.file_url || message.file_url || "";
    const fileName = message.fileName || message.original?.file_name || "";
    const messageType = (() => {
      if (message.type && message.type !== "file") return message.type;
      if (message.message_type === "poll" || message.type === "poll") return "poll";

      if (resolvedMime.startsWith("image")) return "image";
      if (resolvedMime.startsWith("audio")) return "audio";
      if (resolvedMime.startsWith("video")) return "video";

      const fileHint = `${resolvedFileUrl} ${fileName}`.toLowerCase();
      if (/(\.(png|jpe?g|gif|webp|bmp|svg))$/.test(fileHint)) return "image";
      if (/(\.(mp3|wav|ogg|m4a|aac|webm))$/.test(fileHint)) return "audio";
      if (/(\.(mp4|mov|mkv|avi|webm|m4v))$/.test(fileHint)) return "video";

      return message.type || "document";
    })();

    switch (messageType) {
      case "image":
        return <ImageMessage message={message} isUser={isUser} senderName={senderName} onShare={onShare} />;
      case "video":
        return <VideoMessage message={message} isUser={isUser} senderName={senderName} onShare={onShare} />;
      case "document":
        return <DocumentMessage message={message} isUser={isUser} senderName={senderName} onShare={onShare} />;
      case "audio":
        return <AudioMessage message={message} isUser={isUser} senderName={senderName} />;
      case "poll":
        return <PollMessage message={message} isUser={isUser} senderName={senderName} />;
      default:
        return <TextMessage message={message} isUser={isUser} senderName={senderName} />;
    }
  }

  useEffect(() => {
    function handleClickOutside (event) {
      // Don't close if clicking on file input
      if (event.target.type === "file") {
        return;
      }
      
      const isOutside = containerRef.current && !containerRef.current.contains(event.target);
      const isNotAttachButton = !attachButtonRef.current || !attachButtonRef.current.contains(event.target);
      const isNotVoiceAttachButton = !voiceAttachButtonRef.current || !voiceAttachButtonRef.current.contains(event.target);
      const isNotVoiceRecorder = !voiceRecorderRef.current || !voiceRecorderRef.current.contains(event.target);
      
      if (isOutside && isNotAttachButton && isNotVoiceAttachButton && isNotVoiceRecorder) {
        if (showAttachModal) {
          setShowAttachModal(false);
        }
        if (showVoiceAttachModal) {
          setShowVoiceAttachModal(false);
        }
        if (showVoice) {
          onReturnToInput();
        }
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showAttachModal, showVoiceAttachModal, showVoice, setShowAttachModal, setShowVoiceAttachModal, onReturnToInput]);

  const mobileBottomSafeClass = "";

  return (
    <section
      className="bg-[#F3F0FF] h-full min-h-0 flex flex-col overflow-hidden relative"
      ref={containerRef}
      onDragEnter={handleComposerDragEnter}
      onDragOver={handleComposerDragOver}
      onDragLeave={handleComposerDragLeave}
      onDrop={handleComposerDrop}
    >
      {messageToShare && (
        <ShareMessageModal
          open={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          message={messageToShare}
          contacts={allContacts}
          onShareMessage={onShareMessage}
        />
      )}
      {isFileDragging && (
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center border-2 border-dashed border-[#7C4DFF] bg-[#7C4DFF]/10 backdrop-blur-[1px]">
          <div className="rounded-full bg-[#7C4DFF] px-4 py-2 text-sm font-semibold text-white shadow-lg">
            Drop image or video to send
          </div>
        </div>
      )}
      {/* HEADER */}
      <div className="md:hidden flex shrink-0 items-center sticky top-0 p-2 bg-[#F1F1F1] border-b border-gray-200 z-10">
        <button onClick={onBackMobile}>
          <svg
            className="w-6 h-6 mr-2"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div 
          className="flex items-center cursor-pointer hover:bg-gray-200 rounded-lg p-1 transition-colors flex-1"
          onClick={() => onHeaderClick && onHeaderClick(groupId)}
        >
          <img
            src={groupImageSrc}
            className="h-10 w-10 mr-2 rounded-full"
            alt={groupName}
          />
          <div>
            <h2 className="font-semibold text-gray-900">{groupName}</h2>
          </div>
        </div>
      </div>
      <div className="hidden md:flex bg-[#F1F1F1] border-b border-gray-200 p-4 items-center sticky top-0">
        <div 
          className="flex items-center cursor-pointer rounded-lg p-2 transition-colors"
          onClick={() => onHeaderClick && onHeaderClick(groupId)}
        >
          <img
            src={groupImageSrc}
            className="h-12 w-12 mr-3 rounded-full"
            alt={groupName}
          />
          <div>
            <h2 className="font-semibold text-gray-900">{groupName}</h2>
          </div>
        </div>
      </div>

      {/* MESSAGES */}
      <div className="mobile-scroll-y flex-1 min-h-0 p-4 md:pb-4 space-y-4">
        {messages.map((message, index) => {
          // Check if day changed from previous message
          const currentDate = resolveMessageCalendarDate(message);
          const previousMessage = index > 0 ? messages[index - 1] : null;
          const previousDate = previousMessage ? resolveMessageCalendarDate(previousMessage) : null;
          const showDateDivider = !previousDate || (currentDate && previousDate && currentDate.getTime() !== previousDate.getTime());
          return (
            <React.Fragment key={message.id}>
              {showDateDivider && currentDate && (
                <div className="flex justify-center items-center my-6">
                  <div className="bg-gray-300/60 px-4 py-1.5 rounded-full text-xs text-gray-700 font-medium">
                    {formatDateDivider(currentDate)}
                  </div>
                </div>
              )}
              <div
                className={`flex ${message.isUser ? "justify-end" : "justify-start"} mb-4`}
              >
                {!message.isUser && (
                  <img
                    src={(message.original?.senderAvatar || message.senderAvatar || message.sender_avatar || "")
                      ? ((message.original?.senderAvatar || message.senderAvatar || message.sender_avatar).startsWith("http")
                        ? (message.original?.senderAvatar || message.senderAvatar || message.sender_avatar)
                        : `${process.env.REACT_APP_API_BASE_URL}${message.original?.senderAvatar || message.senderAvatar || message.sender_avatar}`)
                      : "/profile-picture.png"}
                    alt={message.original?.sender_name || message.sender_name || "User"}
                    className="w-8 h-8 rounded-full object-cover mr-2 flex-shrink-0"
                    onError={(e) => { e.target.src = '/profile-picture.png'; }}
                  />
                )}
                <MessageRenderer message={message} senderName={!message.isUser ? (message.original?.sender_name || message.sender_name || "") : null} />
              </div>
            </React.Fragment>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* INPUT BAR */}
                    <div
                      className={`${showVoice ? "bg-[#F1F1F1]" : "bg-white"} z-10 p-3 md:p-4 ${mobileBottomSafeClass} shrink-0 transition-colors`}
                    >
        <div className="relative">
        {/* Upload Progress Bar */}
        {/* {isUploading && (
          <div className="mb-3 bg-gray-100 rounded-full h-2 overflow-hidden">
            <div
              className="bg-gradient-to-r from-purple-500 to-purple-600 h-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        )}
        {isUploading && (
          <div className="text-xs text-gray-600 mb-3 text-center font-medium">
            Uploading... {uploadProgress}%
          </div>
        )} */}
        
        <div className="flex items-center space-x-3 relative min-h-[35px]">
          {showVoice ?  (
           <>
              <VoiceRecorder
                ref={voiceRecorderRef}
                attachButtonRef={voiceAttachButtonRef}
                time={voiceTime}
                onMicClick={onSendVoiceClick}
                onCancel={onReturnToInput}
                onAttachmentClick={() => setShowVoiceAttachModal(!showVoiceAttachModal)}
              />
              <AttachmentModal
                open={showVoiceAttachModal}
                onClose={() => setShowVoiceAttachModal(false)}
                onPick={handleAttachPick}
                anchorRef={voiceAttachButtonRef}
                position="voice"
                onFileSelect={handleFileInput}
                groupId={groupId}
              />
            </>
          ) :(
               <>
              <button
                aria-label="Add attachment"
                ref={attachButtonRef}
                className="md:h-10 md:w-11 w-11 h-8 p-0 rounded-full bg-gray-100 hover:bg-gray-200 flex justify-center items-center relative"
                onClick={() => setShowAttachModal(!showAttachModal)}
              >
                <img src="../chatscreen/plus.svg" alt="plus" className="md:w-8 md:h-8 w-6 h-6" />
              </button>

              <AttachmentModal
                open={showAttachModal}
                onClose={() => setShowAttachModal(false)}
                onPick={handleAttachPick}
                anchorRef={attachButtonRef}
                position="input"
                onFileSelect={handleFileInput}
                groupId={groupId}
              />

              <input
                ref={chatInputRef}
                id="chat"
                type="text"
                placeholder="Write Here...."
                readOnly={isMobileLayout && showEmoji}
                className="w-full rounded-[100px] border border-gray-400 bg-white px-4 py-3 text-base text-gray-800 placeholder:text-gray-400 outline-none transition focus:border-[#7C4DFF] focus:ring-4 focus:ring-[#7C4DFF]/15 shadow-sm"
                value={message || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setMessage(val.charAt(0) ? val.charAt(0).toUpperCase() + val.slice(1) : "");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    onSendMessage();
                  }
                }}
              />

              <button
                type="button"
                ref={emojiButtonRef}
                className="absolute right-16 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                onMouseDown={(e) => {
                  if (isMobileLayout) e.preventDefault();
                }}
                onClick={handleEmojiToggle}
                aria-expanded={showEmoji}
                aria-label="Toggle emoji picker"
              >
                <img src="../chatscreen/emoji.svg" alt="emoji" className="w-8 h-8" />
              </button>

              <EmojiPickerPopover
                open={showEmoji}
                anchorRef={emojiButtonRef}
                containerRef={containerRef}
                pickerRef={emojiRef}
                onEmojiClick={(emojiData) => {
                  const char = emojiData?.emoji || emojiData?.native || emojiData?.unified || "";
                  setMessage((m) => (m || "") + char);
                  setShowEmoji(false);
                }}
              />

              {message && message.trim() ? (
                <button
                  type="button"
                  className="h-10 w-10 p-0 rounded-full"
                  onClick={onSendMessage}
                  title="Send message"
                >
                  <img src="../chatscreen/send-arrow.svg" alt="send" className="w-8 h-8" />
                </button>
              ) : (
                <button
                  className="h-10 w-10 p-0 rounded-full"
                  aria-label="Record voice"
                  onClick={onMicButton}
                >
                  <img src="../chatscreen/mic.svg" alt="mic" className="w-8 h-8" />
                </button>
              )}
            </>
          ) }
        </div>
        </div>
      </div>
      <PollResultModal open={showPollResultModal}
        onClose={() => setShowPollResultModal(false)}
        poll={selectedPoll} />

      {/* Image Preview Modal */}
      {viewOncePreview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-sm p-4"
          onClick={() => setViewOncePreview(null)}
        >
          <div
            className="relative flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setViewOncePreview(null)}
              className="absolute top-3 right-2 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors z-10"
              aria-label="Close view once image"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-gray-900"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <img
              src={viewOncePreview.src}
              alt="View once"
              style={{ filter: blurViewOnce ? "blur(20px)" : "none" }}
              className="max-w-[90vw] max-h-[60vh] min-h-[200px] min-w-[200px] object-contain rounded-lg shadow-2xl"
              onError={(e) => { e.target.src = "../chatscreen/sample-image.jpg"; }}
              draggable={false}
            />

            {blurViewOnce && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="text-2xl font-bold text-white bg-black/70 px-6 py-3 rounded-xl">Tab inactive</span>
              </div>
            )}
          </div>
        </div>
      )}

      {imagePreview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 backdrop-blur-sm p-4"
          onClick={() => setImagePreview(null)}
        >
          <div
            className="relative flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setImagePreview(null)}
              className="absolute top-3 right-2 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Close image preview"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3 w-3 text-gray-900"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Image */}
            <img
              src={imagePreview}
              alt="Full preview"
              className="max-w-[90vw] max-h-[60vh] min-h-[200px] min-w-[200px] object-contain rounded-lg shadow-2xl"
              onError={(e) => {
                e.target.src = "../chatscreen/sample-image.jpg";
              }}
            />

            {/* Download Button */}
            <a
              href={imagePreview}
              download="image"
              className="absolute bottom-3 left-2 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors shadow-lg"
              title="Download image"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3 w-3 text-gray-900"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v4a2 2 0 002 2h12a2 2 0 002-2v-4m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
            </a>
          </div>
        </div>
      )}
    </section>
  );
}
